import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { GoogleAccessManager } from '../../components/GoogleAccessManager';
import axios from 'axios';
import { getAuthHeaders } from '../../utils/auth';
import { toast } from 'sonner';
import {
  Settings as SettingsIcon,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Link as LinkIcon,
  Database,
  Clock,
  Shield
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Integrations = () => {
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [plaidStatus, setPlaidStatus] = useState(null);

  useEffect(() => {
    fetchPlaidStatus();
  }, []);

  const fetchPlaidStatus = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/plaid/status`, { headers: getAuthHeaders() });
      setPlaidStatus(res.data);
    } catch (error) {
      console.error('Failed to fetch Plaid status:', error);
      toast.error('Failed to load Plaid status');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      const res = await axios.post(`${API}/plaid/link-token`, {}, { headers: getAuthHeaders() });
      
      if (res.data && res.data.link_token) {
        toast.success('✅ Connection test successful! Plaid is configured correctly.');
      } else {
        toast.error('❌ Connection test failed. Invalid response from Plaid.');
      }
      
      // Refresh status after test
      await fetchPlaidStatus();
    } catch (error) {
      console.error('Connection test failed:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      toast.error(`❌ Connection test failed: ${errorMsg}`);
    } finally {
      setTesting(false);
    }
  };

  const getStatusBadge = () => {
    if (!plaidStatus) return null;

    if (!plaidStatus.configured) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-sm">
          <XCircle className="w-4 h-4" />
          Not Configured
        </div>
      );
    }

    if (plaidStatus.connected) {
      return (
        <div className="flex items-center gap-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
          <CheckCircle className="w-4 h-4" />
          Connected
        </div>
      );
    }

    return (
      <div className="flex items-center gap-2 px-3 py-1 bg-yellow-500/20 text-yellow-400 rounded-full text-sm">
        <AlertTriangle className="w-4 h-4" />
        Configured, Not Connected
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <RefreshCw className="w-12 h-12 animate-spin text-[#F59E0B]" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3" data-testid="integrations-page">
            <SettingsIcon className="text-[#F59E0B]" />
            Integrations
          </h1>
          <p className="text-gray-400 mt-1">Manage external service connections</p>
        </div>

        {/* Google sign-in access (owner-managed allowlist) */}
        <GoogleAccessManager />

        {/* Plaid Integration Card */}
        <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-lg flex items-center justify-center">
                <Database className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Plaid Financial Aggregation</h2>
                <p className="text-gray-400 text-sm mt-1">
                  Connect banks and brokerages for real-time financial data
                </p>
              </div>
            </div>
            {getStatusBadge()}
          </div>

          {/* Status Details */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-[#0A0A0A] rounded-lg border border-gray-800">
            <div>
              <div className="text-gray-400 text-xs mb-1">Environment</div>
              <div className="text-white font-medium capitalize" data-testid="plaid-environment">
                {plaidStatus?.environment || 'Unknown'}
              </div>
            </div>
            <div>
              <div className="text-gray-400 text-xs mb-1">Configured</div>
              <div className={`font-medium ${plaidStatus?.configured ? 'text-green-400' : 'text-red-400'}`} data-testid="plaid-configured">
                {plaidStatus?.configured ? 'Yes' : 'No'}
              </div>
            </div>
            <div>
              <div className="text-gray-400 text-xs mb-1">Institutions</div>
              <div className="text-white font-medium" data-testid="plaid-institutions-count">
                {plaidStatus?.institutions_count || 0}
              </div>
            </div>
            <div>
              <div className="text-gray-400 text-xs mb-1">Accounts</div>
              <div className="text-white font-medium" data-testid="plaid-accounts-count">
                {plaidStatus?.accounts_count || 0}
              </div>
            </div>
          </div>

          {/* Last Sync */}
          {plaidStatus?.last_sync && (
            <div className="mb-4 p-3 bg-[#0A0A0A] rounded-lg border border-gray-800">
              <div className="flex items-center gap-2 text-sm">
                <Clock className="w-4 h-4 text-gray-400" />
                <span className="text-gray-400">Last sync:</span>
                <span className="text-white" data-testid="plaid-last-sync">
                  {new Date(plaidStatus.last_sync).toLocaleString()}
                </span>
              </div>
            </div>
          )}

          {/* Last Error */}
          {plaidStatus?.last_error && (
            <div className="mb-4 p-3 bg-red-500/10 rounded-lg border border-red-500/30">
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <span className="text-red-400 font-medium">Last Error:</span>
                  <p className="text-red-300 mt-1" data-testid="plaid-last-error">
                    {plaidStatus.last_error.message}
                  </p>
                  {plaidStatus.last_error.timestamp && (
                    <p className="text-red-400/60 text-xs mt-1">
                      {new Date(plaidStatus.last_error.timestamp).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Configuration Warning */}
          {!plaidStatus?.configured && (
            <div className="mb-4 p-4 bg-yellow-500/10 rounded-lg border border-yellow-500/30">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-yellow-400 font-medium mb-2">Plaid is not configured</p>
                  <p className="text-yellow-300/80">
                    To enable Plaid integration, add the following environment variables to your backend:
                  </p>
                  <ul className="mt-2 space-y-1 text-yellow-300/80 list-disc list-inside">
                    <li><code className="text-xs bg-black/30 px-1 py-0.5 rounded">PLAID_CLIENT_ID</code></li>
                    <li><code className="text-xs bg-black/30 px-1 py-0.5 rounded">PLAID_SECRET</code></li>
                    <li><code className="text-xs bg-black/30 px-1 py-0.5 rounded">PLAID_ENV</code> (sandbox or production)</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Production Readiness Guide */}
          {plaidStatus?.configured && plaidStatus?.environment === 'sandbox' && (
            <div className="mb-4 p-4 bg-blue-500/10 rounded-lg border border-blue-500/30">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="text-blue-400 font-medium mb-2">Running in Sandbox Mode</p>
                  <p className="text-blue-300/80 mb-2">
                    To switch to production:
                  </p>
                  <ol className="space-y-1 text-blue-300/80 list-decimal list-inside">
                    <li>Get production credentials from Plaid Dashboard</li>
                    <li>Update <code className="text-xs bg-black/30 px-1 py-0.5 rounded">PLAID_ENV=production</code></li>
                    <li>Update <code className="text-xs bg-black/30 px-1 py-0.5 rounded">PLAID_CLIENT_ID</code> and <code className="text-xs bg-black/30 px-1 py-0.5 rounded">PLAID_SECRET</code></li>
                    <li>Restart backend server</li>
                    <li>Test connection below</li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3">
            <button
              onClick={handleTestConnection}
              disabled={testing || !plaidStatus?.configured}
              className="flex-1 px-4 py-3 bg-[#F59E0B] hover:bg-[#F59E0B]/80 text-black rounded-lg flex items-center justify-center gap-2 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="test-connection-btn"
            >
              <LinkIcon className={`w-5 h-5 ${testing ? 'animate-pulse' : ''}`} />
              {testing ? 'Testing Connection...' : 'Test Connection'}
            </button>
            <button
              onClick={fetchPlaidStatus}
              disabled={loading}
              className="px-4 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-2"
              data-testid="refresh-status-btn"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Info Notice */}
        <div className="bg-[#1C1C1E] border border-gray-800 rounded-lg p-4">
          <div className="flex items-start gap-3 text-sm">
            <Shield className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <div className="text-gray-400">
              <strong className="text-white">Security Notice:</strong> All API credentials are stored securely in environment variables. 
              No secrets are exposed through this interface. All integration activity is logged in the audit trail.
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Integrations;
